import React, { FCWithChildren } from 'react';
import clsx from 'clsx';
import { TFunction } from 'next-i18next';
import Button, { ButtonProps } from '@components/atoms/button/button';
import { ResponseResult } from '@services/my-orders-sales-service';
import Dialog from '@components/atoms/dialog/dialog';
import Icon from '@components/atoms/icon/icon';
import styles from '../popin.module.scss';

export function getErrorString<D>(t: TFunction, responseResult: ResponseResult<D>): string | null {
  switch (responseResult.kind) {
    case 'ServerError':
      return responseResult.message;
    case 'InvalidData':
    case 'NetworkingError':
      return t('MY_ACCOUNT.ORDERS_SALES.ERROR.API');
    case 'Success':
      return null;
  }
}

export const OkButtonView: FCWithChildren<{
  isSubmitting: boolean;
  onClick: () => void;
  disabled?: boolean;
}> = ({ isSubmitting, onClick, disabled, children }) => {
  return (
    <Button
      variant="primary"
      size="medium"
      className={clsx(
        styles.ordersSales__popin__buttons__button,
        isSubmitting ? styles['ordersSales__popin__buttons__button--loading'] : '',
      )}
      disabled={disabled || isSubmitting}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

export const CancelButtonView: FCWithChildren<{
  isSubmitting: boolean;
  onClick: () => void;
}> = ({ isSubmitting, onClick, children }) => {
  return (
    <Button
      variant="secondary"
      size="medium"
      className={clsx(styles.ordersSales__popin__buttons__button)}
      disabled={isSubmitting}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

export const OutlineButtonView: FCWithChildren<
  {
    onClick: () => void;
  } & Omit<ButtonProps, 'onClick'>
> = ({ onClick, children, ...props }) => {
  return (
    <Button
      variant="ghost"
      size="medium"
      className={clsx(styles.ordersSales__popin__buttons__button, styles.ordersSales__popin__buttons__outline)}
      onClick={onClick}
      {...props}
    >
      {children}
    </Button>
  );
};

export const OkView: React.FC<{
  message: string;
}> = ({ message }) => {
  return (
    <div className={styles.ordersSales__popin__success}>
      <div className={styles.ordersSales__popin__success__icon}>
        <Icon
          name="tick-32"
          className={styles.ordersSales__popin__success__icon__svg}
          ariaHidden
        />
      </div>
      <div className={styles.ordersSales__popin__success__text}>{message}</div>
    </div>
  );
};

export const KoView: React.FC<{
  message: string;
}> = ({ message }) => {
  return (
    <div className={styles.ordersSales__popin__error}>
      <div className={styles.ordersSales__popin__error__icon}>
        <Icon
          name="cross-x"
          className={styles.ordersSales__popin__error__icon__svg}
          ariaHidden
        />
      </div>
      <div className={styles.ordersSales__popin__error__text}>{message}</div>
    </div>
  );
};

export const PopinView: FCWithChildren<{
  closePopin: () => void;
  title: string | React.JSX.Element;
}> = ({ closePopin, title, children }) => {
  return (
    <Dialog
      closeDialog={closePopin}
      key={`orderSalesPopinView`}
      isOpen={true}
      hasCloseIcon
      customClass={styles.ordersSales__popin__container}
    >
      {(modalDialogTitleId) => (
        <>
          {typeof title === 'string' ? (
            <h2
              className={clsx('vc-modal__title', styles.ordersSales__popin__title)}
              id={modalDialogTitleId}
            >
              {title}
            </h2>
          ) : (
            title
          )}
          <div className={clsx('vc-modal__content', styles.ordersSales__popin__content)}>{children}</div>
        </>
      )}
    </Dialog>
  );
};
